import React, { useEffect, useState } from 'react';

import { T } from 'services/lang/LanguageService';
import { DashboardReportsApi } from "api/DashboardReportsApi";
import { useGetDashboardsInfo } from "api/api-hooks/dashboards-hooks";
import { useGenerateReport, useMarkForRegenerationRange } from "api/api-hooks/reports-hooks";
import { DashboardInfos } from "api/model/dashboard/DashboardInfos";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { TemplateLoadingType } from "utils/enums";
import { easasAlert } from 'components/fragments/easas-global-fragment/EasasGlobalAlerts';
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import { IPeriodLoadingConfig } from "../product-template-table/utils/interfaces";
import { getTemplateLoadingRange } from "../product-template-table/utils/timeUtils";

type GenerateReportDialogType = {
    ids: Array<number>
    loadingConfig?: IPeriodLoadingConfig
    close(): void
}

const methods = [
    {label: T("word.single"), value: false},
    {label: T("word.range"), value: true}
];

const GenerateNewReport = (props: GenerateReportDialogType) => {
    const [method, setMethod] = useState(false)
    const [dashboard, setDashboard] = useState<DashboardInfos | undefined>(undefined);
    const [fromDate, setFromDate] = useState<Date | undefined>();
    const [toDate, setToDate] = useState<Date | undefined>();
    const [fillMissingSlots, setFillMissingSlots] = useState(false)
    const [generateNearest, setGenerateNearest] = useState(true)

    const {data, isLoading} = useGetDashboardsInfo(props.ids);

    const {mutate: generateNewReport} = useGenerateReport({
        onSuccess: () => {
            easasAlert({ summary: T("alert.report-table.report-generated-successfully"), severity: "success" });
        },
        onFailure: () => {
            easasAlert({ summary: T("alert.report-table.error-while-generating-report"), severity: "error" });
        },
        onMutate: () => {
            easasAlert(T("alert.report-table.report-generation-initiated"))
            props.close()
        }
    });


    const {mutate: regenerateRange} = useMarkForRegenerationRange({
        onSuccess: () => {
            easasAlert({ summary: T("alert.report-table.report-range-marked-successfully"), severity: "success" });
        },
        onFailure: () => {
            easasAlert({ summary: T("alert.report-table.error-while-marking-generation-range"), severity: "error" });
        },
        onMutate: () => {
            easasAlert(T("alert.report-table.report-range-marking-initiated"))
            props.close()
        }
    });

    useEffect(() => {
        const {start, end} = getTemplateLoadingRange({
            loadingType: TemplateLoadingType.RANGE,
            loadingConfig: props.loadingConfig
        });

        setFromDate(start || new Date());
        setToDate(end || new Date());
    }, [])

    useEffect(() => {
        if (data && !isLoading && data.length === 1) {
            setDashboard(data[0])
        }
    }, [data, isLoading])

    const onDashboardChange = (e: { value: any }) => {
        setDashboard(e.value);
    }

    const onGenerateClick = (generateOnly?: boolean) => {
        if (!dashboard) {
            easasAlert({ summary: T("alert.warning"), detail: T("alert.report-table.no-dashboard-selected"), severity: "warn" });
            return
        }

        if (generateOnly) {
            generateNewReport({
                dashboardId: dashboard.id,
                referenceTime: toDate?.toISOString() || new Date().toISOString(),
                generateNearest
            })
        } else {
            window.open(DashboardReportsApi.getGenerationAndDownloadUrl({
                dashboardId: dashboard.id,
                referenceTime: toDate?.toISOString() || new Date().toISOString(),
                generateOnly: false,
                generateNearest
            }))

            props.close()
        }

    }

    const onRegenerateClick = () => {
        regenerateRange({
            dashboardId: dashboard!!.id,
            fillMissingSlots,
            from: fromDate?.toISOString() || new Date().toISOString(),
            to: toDate?.toISOString() || new Date().toISOString()
        })
    }

    if (isLoading) {
        return <ProgressSpinner/>
    }

    return (
      <div className="flex flex-column gap-3">
        <div className="flex gap-2 align-items-center pt-1">
          <SelectButton value={method} options={methods} onChange={e => setMethod(e.value)} />
        </div>
        <div className="flex flex-column">
          <label htmlFor="report">
            <b>{T("word.dashboard")}</b>
          </label>
          {props.ids.length > 1 && props.ids.length !== 0 ? (
            <Dropdown
              value={dashboard}
              options={data}
              onChange={onDashboardChange}
              optionLabel={T("word.title")}
              placeholder={T("action.select-a-dashboard")}
            />
          ) : (
            <InputText value={dashboard?.title || ""} disabled />
          )}
        </div>

        {method ? (
          <div className="flex flex-column gap-1">
            <div style={{ fontWeight: "bold", fontSize: "larger", paddingTop: "5px" }}>
              {T("load-conf.reference-time")}
            </div>
            <div className="flex flex-column">
              <label htmlFor="from">
                <b>{T("word.from")}</b>
              </label>
              <CalendarWrapper id="from" value={fromDate || new Date()} onChange={setFromDate} />
            </div>
            <div className="flex flex-column">
              <label htmlFor="to">
              <b>{T("word.to")}</b>
              </label>
              <CalendarWrapper id="to" value={toDate || new Date()} onChange={setToDate} />
            </div>
          </div>
        ) : (
          <div className="flex flex-column">
            <label htmlFor="referenceName">
              <b>{T("load-conf.reference-time")}</b>
            </label>
            <CalendarWrapper id="referenceName" value={toDate || new Date()} onChange={setToDate} />
          </div>
        )}

        {method ? (
          <div className="flex gap-1 align-items-center">
            <label htmlFor="fillMissing">
              <b>{T("message.report-table.fill-missing-slots")}</b>
            </label>
            <InputSwitch
              id="fillMissing"
              checked={fillMissingSlots}
              onChange={e => setFillMissingSlots(e.value)}
            />
          </div>
        ) : (
          <div className="flex gap-2 align-items-center">
            <label htmlFor="generateNearest">
              <b>{T("action.report-table.generate nearest")}</b>
            </label>
            <InputSwitch
              id="generateNearest"
              checked={generateNearest}
              onChange={e => setGenerateNearest(e.value)}
            />
          </div>
        )}

        {method ? (
          <Button label={T("action.generate")} onClick={() => onRegenerateClick()} disabled={!dashboard} />
        ) : (
          <div className="flex gap-1">
            <Button label={T("action.generate")} onClick={() => onGenerateClick(true)} disabled={!dashboard} />
            <Button
              label={T("action.report-table.generate-and-download")}
              onClick={() => onGenerateClick()}
              disabled={!dashboard}
            />
          </div>
        )}
      </div>
    );
};

export default GenerateNewReport;